import { useState } from 'react';
import { Modal, Button, Input, Select, Chip, useToast } from '../../../shared/components';
import { extractCalendarFromImage, ExtractedDay, MarkingConfig } from '../../../shared/services/humanResources/calendarOcr';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';

const repo = getHumanResourcesRepository();

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const WEEKDAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

function weekdayOf(year: number, month: number, day: number): string {
  return WEEKDAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
}
function quincenaOf(month: number, day: number): string {
  return `${day <= 15 ? '1Q' : '2Q'} ${MONTHS[month - 1]}`;
}

type Step = 'upload' | 'months' | 'processing' | 'preview';

/**
 * Asistente de carga de calendario por imagen. Todo el OCR y el analisis de
 * color/forma corren en ESTE navegador (Tesseract.js) - el backend solo
 * recibe el resultado ya estructurado, para caer en la tabla de staging
 * (revision humana obligatoria antes de confirmar - ver StagingReviewTab).
 */
export function CalendarImportModal({ open, onClose, defaultYear, onImported }: { open: boolean; onClose: () => void; defaultYear: number; onImported: () => void }) {
  const toast = useToast();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [year, setYear] = useState(defaultYear);
  const [markingType, setMarkingType] = useState<'color' | 'shape'>('color');
  const [targetColorHex, setTargetColorHex] = useState('#FF0000');
  const [shapeType, setShapeType] = useState<'circle' | 'square'>('circle');
  const [processAll, setProcessAll] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [monthToAdd, setMonthToAdd] = useState('1');
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<Map<number, ExtractedDay[]> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setProcessAll(true);
    setSelectedMonths([]);
    setResults(null);
    setProgress(0);
  };
  const close = () => {
    reset();
    onClose();
  };

  const addMonth = () => {
    const m = Number(monthToAdd);
    if (!selectedMonths.includes(m)) setSelectedMonths((ms) => [...ms, m].sort((a, b) => a - b));
  };
  const removeMonth = (m: number) => setSelectedMonths((ms) => ms.filter((x) => x !== m));

  const runExtraction = async () => {
    if (!file) return;
    setStep('processing');
    setProgress(0);
    const months = processAll ? Array.from({ length: 12 }, (_, i) => i + 1) : selectedMonths;
    const config: MarkingConfig = { markingType, targetColorHex: markingType === 'color' ? targetColorHex : undefined, shapeType: markingType === 'shape' ? shapeType : undefined };
    try {
      const extracted = await extractCalendarFromImage(file, config, months, setProgress);
      setResults(extracted);
      setStep('preview');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo procesar la imagen.', 'error');
      setStep('months');
    }
  };

  const submitBatches = async () => {
    if (!results) return;
    setIsSubmitting(true);
    try {
      let totalRows = 0;
      for (const [month, days] of results) {
        if (days.length === 0) continue;
        await repo.createStagingBatch({
          sourceYear: year,
          sourceMonth: month,
          rows: days.map((d) => ({
            calendarDate: `${year}-${String(month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`,
            dayOfWeek: weekdayOf(year, month, d.day),
            dayType: d.isMarked ? 'Festivo' : 'Ordinario',
            quincena: quincenaOf(month, d.day),
            confidence: d.confidence,
          })),
        });
        totalRows += days.length;
      }
      toast.show(`Se cargaron ${totalRows} días a revisión. Revísalos en "Pendiente de revisión" antes de confirmar.`);
      close();
      onImported();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo enviar el lote a revisión.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const monthsWithZeroResults = results ? Array.from(results.entries()).filter(([, days]) => days.length === 0).map(([m]) => m) : [];

  return (
    <Modal open={open} onClose={close} title="Cargar calendario por imagen">
      {step === 'upload' && (
        <div className="space-y-4">
          <Input label="Año que corresponde a esta imagen" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          <label className="block w-full">
            <span className="mb-1.5 block text-sm font-medium text-ink-700">Imagen del calendario (con los 12 meses)</span>
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="block w-full text-sm text-ink-600" />
          </label>

          <div className="rounded-xl border border-ink-100 p-3">
            <p className="mb-2 text-sm font-medium text-ink-700">¿Cómo están marcados los festivos en esta imagen?</p>
            <div className="flex gap-2">
              <Button variant={markingType === 'color' ? 'primary' : 'outline'} size="md" onClick={() => setMarkingType('color')}>Por color</Button>
              <Button variant={markingType === 'shape' ? 'primary' : 'outline'} size="md" onClick={() => setMarkingType('shape')}>Bordeado (círculo/cuadro)</Button>
            </div>
            {markingType === 'color' ? (
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm text-ink-600">Color del festivo:</span>
                <input type="color" value={targetColorHex} onChange={(e) => setTargetColorHex(e.target.value)} className="h-9 w-14 rounded border border-ink-200" />
              </div>
            ) : (
              <div className="mt-3">
                <Select
                  options={[{ value: 'circle', label: 'Círculo' }, { value: 'square', label: 'Cuadro / rectángulo' }]}
                  value={shapeType}
                  onChange={(e) => setShapeType(e.target.value as 'circle' | 'square')}
                />
              </div>
            )}
          </div>

          <p className="text-xs text-ink-400">
            La lectura automática es una primera pasada — algunos días pueden quedar mal clasificados. Por eso caen primero en "Pendiente de revisión": ahí
            puedes corregir cualquier error antes de confirmarlos.
          </p>

          <Button fullWidth disabled={!file} onClick={() => setStep('months')}>
            Siguiente
          </Button>
        </div>
      )}

      {step === 'months' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button variant={processAll ? 'primary' : 'outline'} size="md" onClick={() => setProcessAll(true)}>Procesar todo el año</Button>
            <Button variant={!processAll ? 'primary' : 'outline'} size="md" onClick={() => setProcessAll(false)}>Procesar meses específicos</Button>
          </div>

          {!processAll && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Select options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))} value={monthToAdd} onChange={(e) => setMonthToAdd(e.target.value)} className="flex-1" />
                <Button size="md" onClick={addMonth}>Agregar</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedMonths.length === 0 && <p className="text-sm text-ink-400">Sin meses agregados todavía.</p>}
                {selectedMonths.map((m) => (
                  <Chip key={m} label={MONTHS[m - 1]} onRemove={() => removeMonth(m)} />
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setStep('upload')}>Atrás</Button>
            <Button fullWidth disabled={!processAll && selectedMonths.length === 0} onClick={runExtraction}>
              Procesar imagen
            </Button>
          </div>
        </div>
      )}

      {step === 'processing' && (
        <div className="space-y-4 py-6 text-center">
          <p className="text-sm text-ink-600">Leyendo la imagen en tu navegador (Tesseract.js)…</p>
          <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
            <div className="h-full bg-brand-500 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-ink-400">{progress}%</p>
        </div>
      )}

      {step === 'preview' && results && (
        <div className="space-y-4">
          <p className="text-sm text-ink-600">Lectura completada. Resultados por mes:</p>
          <div className="space-y-1.5">
            {Array.from(results.entries()).map(([month, days]) => (
              <div key={month} className="flex items-center justify-between rounded-lg border border-ink-100 px-3 py-2 text-sm">
                <span>{MONTHS[month - 1]}</span>
                <span className={days.length === 0 ? 'text-critical' : 'text-ink-600'}>
                  {days.length === 0 ? 'No se encontraron días — revisar imagen' : `${days.length} días · ${days.filter((d) => d.isMarked).length} festivos`}
                </span>
              </div>
            ))}
          </div>
          {monthsWithZeroResults.length > 0 && (
            <p className="text-xs text-critical">
              {monthsWithZeroResults.map((m) => MONTHS[m - 1]).join(', ')} no se pudieron ubicar en la imagen (encabezado del mes no reconocido). Puedes
              seguir con los demás y cargar esos manualmente, o intentar con otra imagen.
            </p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setStep('months')}>Atrás</Button>
            <Button fullWidth onClick={submitBatches} isLoading={isSubmitting}>
              Enviar a revisión
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
